<template>
  <v-dialog v-model="dialog" scrollable width="750">
    <v-card v-if="item">
      <v-toolbar color="primary" dense dark class="elevation-0">
        <v-toolbar-title>Phiếu mượn {{ item.code }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon dark @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-6">
        <v-alert v-if="isOverdue" type="error" text dense class="mb-3">
          <v-icon left small color="error">mdi-alert-circle</v-icon>
          <strong>Cảnh báo:</strong> Phiếu mượn này đã quá hạn trả dự kiến (Hạn trả: {{ item.expectedReturnDate }} {{ item.expectedReturnTime || '17:00' }}).
        </v-alert>

        <v-row dense class="mb-3">
          <v-col cols="6">
            <span class="grey--text">Mã phiếu:</span>
            <strong class="primary--text ml-2">{{ item.code }}</strong>
          </v-col>
          <v-col cols="6">
            <span class="grey--text">Trạng thái:</span>
            <v-chip x-small :color="getStatusColor(item)" dark class="ml-2">
              {{ getStatusLabel(item) }}
            </v-chip>
          </v-col>
          <v-col cols="6">
            <span class="grey--text">Người mượn:</span>
            <strong class="ml-2">{{ item.borrowerName }}</strong>
          </v-col>
          <v-col cols="6">
            <span class="grey--text">SĐT:</span>
            <span class="ml-2">{{ item.borrowerPhone || '-' }}</span>
          </v-col>
          <v-col cols="6">
            <span class="grey--text">Thời gian mượn:</span>
            <span class="ml-2 font-weight-medium">{{ (item.borrowTime ? item.borrowTime + ' ' : '') + (item.borrowDate | ddmmyyyy) }}</span>
          </v-col>
          <v-col cols="6">
            <span class="grey--text">Hạn trả dự kiến:</span>
            <span class="ml-2 font-weight-medium" :class="{ 'red--text font-weight-bold': isOverdue }">
              {{ (item.expectedReturnTime ? item.expectedReturnTime + ' ' : '') + (item.expectedReturnDate | ddmmyyyy) }}
            </span>
          </v-col>
          <v-col cols="12">
            <span class="grey--text">Mục đích sử dụng:</span>
            <span class="ml-2 font-weight-medium">{{ item.purpose }}</span>
          </v-col>
        </v-row>

        <v-divider class="my-3" />

        <div class="text-subtitle-2 font-weight-bold mb-2">DANH SÁCH THIẾT BỊ MƯỢN</div>
        <v-simple-table dense class="elevation-1">
          <template v-slot:default>
            <thead>
              <tr class="grey lighten-4">
                <th>STT</th>
                <th>Tên thiết bị</th>
                <th class="text-center">Số lượng</th>
                <th class="text-center">Đơn vị</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(it, idx) in item.items || []" :key="idx">
                <td>{{ idx + 1 }}</td>
                <td>{{ it.assetName || it.asset }}</td>
                <td class="text-center font-weight-bold">{{ it.quantity }}</td>
                <td class="text-center">{{ it.unit || 'Chiếc' }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <div v-if="item.handoverNote" class="mt-3">
          <span class="grey--text">Ghi chú bàn giao:</span> {{ item.handoverNote }}
        </div>
        <div v-if="item.returnCondition" class="mt-2">
          <span class="grey--text">Tình trạng khi trả:</span> {{ item.returnCondition }}
        </div>
        <div v-if="item.rejectReason" class="mt-2 red--text">
          <span>Lý do từ chối:</span> {{ item.rejectReason }}
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <!-- Nút thao tác của Quản trị viên -->
        <template v-if="!isTeacher">
          <!-- Nút Duyệt / Từ chối khi PENDING -->
          <template v-if="item.status === 'PENDING'">
            <v-btn color="error" text @click="rejectDialog = true">
              <v-icon left small>mdi-close</v-icon> Từ chối
            </v-btn>
            <v-spacer />
            <v-btn outlined color="grey darken-1" class="mr-2" @click="dialog = false">
              Hủy
            </v-btn>
            <v-btn color="success" depressed :loading="approveLoading" @click="onApprove">
              <v-icon left small>mdi-check</v-icon> Xác nhận duyệt
            </v-btn>
          </template>

          <!-- Nút Bàn giao khi APPROVED -->
          <template v-else-if="item.status === 'APPROVED'">
            <v-btn outlined color="grey darken-1" @click="dialog = false">Đóng</v-btn>
            <v-spacer />
            <v-btn color="primary" depressed @click="onHandover">
              <v-icon left small>mdi-hand-coin</v-icon> Bàn giao thiết bị
            </v-btn>
          </template>

          <!-- Nút Xác nhận Trả khi BORROWING -->
          <template v-else-if="item.status === 'BORROWING'">
            <v-btn outlined color="grey darken-1" @click="dialog = false">Đóng</v-btn>
            <v-spacer />
            <v-btn color="teal" dark depressed @click="onReturn">
              <v-icon left small>mdi-keyboard-return</v-icon> Xác nhận nhận lại thiết bị
            </v-btn>
          </template>

          <template v-else>
            <v-spacer />
            <v-btn outlined @click="dialog = false">Đóng</v-btn>
          </template>
        </template>

        <!-- Nếu là Giáo viên thì chỉ có nút Đóng -->
        <template v-else>
          <v-spacer />
          <v-btn outlined @click="dialog = false">Đóng</v-btn>
        </template>
      </v-card-actions>
    </v-card>

    <!-- Dialog từ chối duyệt -->
    <BorrowRejectDialog :state="rejectDialog" :item="item" @updated="onRejectSuccess" />
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BorrowRejectDialog from '@/modules/facility/borrow/BorrowRejectDialog.vue'

export default {
  name: 'BorrowDetailDialog',
  components: {
    BorrowRejectDialog
  },
  props: {
    state: Boolean,
    item: Object
  },
  data() {
    return {
      dialog: false,
      rejectDialog: false,
      approveLoading: false
    }
  },
  computed: {
    ...mapGetters('auth', ['role']),
    isTeacher() {
      return this.role && this.role.name === 'Teacher'
    },
    isOverdue() {
      if (!this.item || this.item.status === 'RETURNED' || this.item.status === 'REJECTED') return false
      if (!this.item.expectedReturnDate) return false
      const now = new Date()
      const timeStr = this.item.expectedReturnTime || '23:59'
      const returnDateTime = new Date(`${this.item.expectedReturnDate}T${timeStr}:00`)
      return returnDateTime < now
    }
  },
  watch: {
    state(val) {
      if (val) this.dialog = true
    }
  },
  methods: {
    ...mapActions('FacilityBorrows', ['approveBorrow', 'rejectBorrow', 'handoverBorrow', 'returnBorrow']),
    getStatusLabel(item) {
      if (typeof item === 'string') {
        switch (item) {
          case 'PENDING': return 'Chờ duyệt'
          case 'APPROVED': return 'Đã duyệt'
          case 'BORROWING': return 'Đang mượn'
          case 'RETURNED': return 'Đã trả'
          case 'REJECTED': return 'Từ chối'
          case 'OVERDUE': return 'Quá hạn'
          default: return item
        }
      }
      if (item.status === 'RETURNED') return 'Đã trả'
      if (item.status === 'REJECTED') return 'Từ chối'
      if (item.status === 'OVERDUE' || this.isOverdue) {
        if (item.status === 'BORROWING') return 'Quá hạn trả'
        if (item.status === 'APPROVED') return 'Quá hạn nhận'
        return 'Quá hạn'
      }
      switch (item.status) {
        case 'PENDING': return 'Chờ duyệt'
        case 'APPROVED': return 'Đã duyệt'
        case 'BORROWING': return 'Đang mượn'
        default: return item.status
      }
    },
    getStatusColor(item) {
      const status = typeof item === 'string' ? item : item.status
      if (status === 'RETURNED') return 'success'
      if (status === 'REJECTED') return 'blue-grey'
      if (typeof item === 'object' && (status === 'OVERDUE' || this.isOverdue)) {
        return 'error'
      }
      switch (status) {
        case 'PENDING': return 'warning'
        case 'APPROVED': return 'indigo'
        case 'BORROWING': return 'primary'
        case 'OVERDUE': return 'error'
        default: return 'blue-grey'
      }
    },
    async onApprove() {
      if (!this.item || !this.item.id) return
      this.approveLoading = true
      try {
        const res = await this.approveBorrow(this.item.id)
        if (res) {
          this.dialog = false
          this.$emit('updated')
        }
      } finally {
        this.approveLoading = false
      }
    },
    onRejectSuccess() {
      this.dialog = false
      this.$emit('updated')
    },
    async onHandover() {
      await this.handoverBorrow({ id: this.item.id, handoverNote: 'Đã bàn giao đầy đủ' })
      this.dialog = false
      this.$emit('updated')
    },
    async onReturn() {
      await this.returnBorrow({ id: this.item.id, returnCondition: 'Hoạt động tốt', note: '' })
      this.dialog = false
      this.$emit('updated')
    }
  }
}
</script>
